import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Palette, Sparkles, Award, Tag, Lock, Check } from 'lucide-react';
import { 
  COSMETICS, 
  PlayerCosmetics, 
  getCosmeticsByType, 
  isUnlocked,
  getCosmeticById,
  Cosmetic
} from '@/lib/cosmetics';

interface CosmeticsMenuProps {
  level: number;
  selectedCosmetics: PlayerCosmetics;
  onSelect: (cosmetics: PlayerCosmetics) => void;
}

export default function CosmeticsMenu({ level, selectedCosmetics, onSelect }: CosmeticsMenuProps) {
  const [open, setOpen] = useState(false);
  const [localSelection, setLocalSelection] = useState<PlayerCosmetics>(selectedCosmetics);

  const handleSelect = (type: keyof PlayerCosmetics, id: string) => {
    if (!isUnlocked(id, level)) return;
    
    const newSelection = { ...localSelection, [type]: id };
    setLocalSelection(newSelection);
    onSelect(newSelection);
  };

  const renderCosmeticItem = (cosmetic: Cosmetic, type: keyof PlayerCosmetics) => {
    const unlocked = isUnlocked(cosmetic.id, level);
    const selected = localSelection[type] === cosmetic.id;
    
    return (
      <motion.button
        key={cosmetic.id}
        whileHover={unlocked ? { scale: 1.05 } : {}}
        whileTap={unlocked ? { scale: 0.95 } : {}}
        onClick={() => handleSelect(type, cosmetic.id)}
        disabled={!unlocked}
        className={`relative p-4 rounded-xl border-2 transition-all ${
          selected 
            ? 'border-primary bg-primary/10' 
            : unlocked 
              ? 'border-border/50 hover:border-primary/50 bg-secondary/30' 
              : 'border-border/20 bg-secondary/10 opacity-50 cursor-not-allowed'
        }`}
      >
        {/* Selection indicator */}
        {selected && (
          <div className="absolute top-2 right-2">
            <Check className="h-4 w-4 text-primary" />
          </div>
        )}
        
        {/* Lock indicator */}
        {!unlocked && (
          <div className="absolute top-2 right-2 flex items-center gap-1 text-xs text-muted-foreground">
            <Lock className="h-3 w-3" />
            <span>Lv.{cosmetic.unlockLevel}</span>
          </div>
        )}
        
        {/* Content */}
        <div className="text-center space-y-2">
          {cosmetic.type === 'avatar_color' ? (
            <div 
              className="w-12 h-12 rounded-full mx-auto border-2 border-border/50"
              style={{ background: cosmetic.value }}
            />
          ) : cosmetic.type === 'badge' ? (
            <div className="text-4xl">{cosmetic.value}</div>
          ) : cosmetic.type === 'celebration' ? (
            <div className="text-4xl">
              {cosmetic.value === 'confetti' && '🎉'}
              {cosmetic.value === 'fireworks' && '🎆'}
              {cosmetic.value === 'stars' && '⭐'}
              {cosmetic.value === 'emoji' && '🎊'}
            </div>
          ) : (
            <div className="text-lg font-bold text-primary">{cosmetic.value}</div>
          )}
          
          <div>
            <div className="font-bold text-sm">{cosmetic.name}</div>
            <div className="text-xs text-muted-foreground">{cosmetic.description}</div>
          </div>
        </div>
      </motion.button>
    );
  };

  // Get current selections for preview
  const currentBadge = getCosmeticById(localSelection.badge);
  const currentTitle = getCosmeticById(localSelection.title);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="font-bold">
          <Palette className="mr-2 h-4 w-4" />
          Customize
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-black flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-primary" />
            Customize Your Look
          </DialogTitle>
        </DialogHeader>

        {/* Preview */}
        <Card className="p-4 bg-secondary/30">
          <div className="flex items-center justify-center gap-4">
            <div 
              className="w-16 h-16 rounded-full border-4 border-border/50 flex items-center justify-center text-3xl"
              style={{ background: getCosmeticById(localSelection.avatarColor)?.value }}
            >
              {currentBadge?.value}
            </div>
            <div className="text-center">
              <div className="font-black text-lg">Your Preview</div>
              <div className="text-sm text-primary font-bold">{currentTitle?.value}</div>
            </div>
          </div>
        </Card>

        <Tabs defaultValue="colors" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="colors" className="text-xs">
              <Palette className="h-4 w-4 mr-1" />
              Colors
            </TabsTrigger>
            <TabsTrigger value="badges" className="text-xs">
              <Award className="h-4 w-4 mr-1" />
              Badges
            </TabsTrigger>
            <TabsTrigger value="celebrations" className="text-xs">
              <Sparkles className="h-4 w-4 mr-1" />
              Effects
            </TabsTrigger>
            <TabsTrigger value="titles" className="text-xs">
              <Tag className="h-4 w-4 mr-1" />
              Titles
            </TabsTrigger>
          </TabsList>

          <TabsContent value="colors" className="mt-4">
            <div className="grid grid-cols-3 gap-3">
              {getCosmeticsByType('avatar_color').map(c => renderCosmeticItem(c, 'avatarColor'))}
            </div>
          </TabsContent>

          <TabsContent value="badges" className="mt-4">
            <div className="grid grid-cols-3 gap-3">
              {getCosmeticsByType('badge').map(c => renderCosmeticItem(c, 'badge'))}
            </div>
          </TabsContent>

          <TabsContent value="celebrations" className="mt-4">
            <div className="grid grid-cols-2 gap-3">
              {getCosmeticsByType('celebration').map(c => renderCosmeticItem(c, 'celebration'))}
            </div>
          </TabsContent>

          <TabsContent value="titles" className="mt-4">
            <div className="grid grid-cols-2 gap-3">
              {getCosmeticsByType('title').map(c => renderCosmeticItem(c, 'title'))}
            </div>
          </TabsContent>
        </Tabs>

        <p className="text-xs text-center text-muted-foreground">
          Unlock more cosmetics by leveling up! You're currently Level {level}.
        </p>
      </DialogContent>
    </Dialog>
  );
}